/**
 * «Выгодные зоны PRO 2» — вход по снятию ликвидности у зоны.
 *
 * Второй индикатор рядом с «Выгодными зонами PRO», а не его правка:
 * PRO владелец просил не трогать. Зоны — те же, карта ликвидности — та же;
 * отметка — другая: не счёт доводов на касании, а последовательность
 * «цена в зоне → уровень сняли и закрылись обратно → на свече снятия
 * перевесили покупки по рынку → слом». Расчёт — `pro2State` из общих
 * правил: им же сервер будит оповещением.
 *
 * Умолчания — лучшие по замеру 21.10.2026 (research/zones-pro/v2-*.mjs);
 * в настройках можно поменять каждое звено. Табло считает, как правило
 * отработало бы на загруженной истории этой монеты — со стопом, целью и
 * издержками, — и рядом говорит, что показал замер на годах.
 *
 * Карта ликвидности и полосы зон нарисованы здесь своими копиями, а не
 * общими с PRO функциями: те живут внутри описания PRO, и вынести их
 * значило бы править PRO.
 */

import {
  PRO2_DEFAULTS,
  ZONE_FADE,
  ZONE_WARMUP,
  alphaOf,
  earliest,
  halfHeightOf,
  lastAtr,
  liquidityMap,
  PRO2_RR,
  pro2OptionsFrom,
  pro2PlanShown,
  pro2State,
  stepOf,
  zoneAlpha,
  zoneReading,
  zoneSeries,
  type LiquidityLevel,
  type Pro2Mark,
  type Pro2State,
  type ValueZoneSeries,
} from '@specter/rules';
import type { Candle } from '../state/store.js';
import type { IndicatorShape, ReadoutLegendItem, ReadoutRow } from './overlays.js';
import { boolParam, colorParam, numberParam, selectParam, type IndicatorContext, type IndicatorDefinition, type IndicatorParams, type IndicatorParamSpec } from './types.js';
import { TABLE_PARAM, priceWords, situationRows, whenWords, type Situation } from './explain.js';
import { deepOf } from './imbalances.js';

export const ZONES_PRO2_ID = 'zones-pro-2';

const UP = '#2FE6A6';
const DOWN = '#FF4D7A';
const SWEEP = '#4FE3FF';
const DAY = 86_400_000;

/* ------------------------------------------------------------ расчёт с памятью */

interface Computed {
  readonly bars: readonly Candle[];
  readonly zones: ValueZoneSeries;
  readonly levels: readonly LiquidityLevel[];
  readonly state: Pro2State;
}

let memo: { key: string; bars: readonly Candle[]; share: unknown; oi: unknown; value: Computed } | null = null;

function computeOf(chart: readonly Candle[], p: IndicatorParams, context: IndicatorContext): Computed {
  const bars = deepOf(chart, context);
  const share = context.derivative('futuresBuyShare');
  const oi = context.derivative('oi');
  const key = `${context.instrument}|${context.timeframe}|${context.livePartial}|${JSON.stringify(p)}`;
  if (memo && memo.key === key && memo.bars === bars && memo.share === share && memo.oi === oi) return memo.value;
  const zones = zoneSeries(bars);
  const levels = liquidityMap(bars, { levels: numberParam(p, 'levels', 400), age: numberParam(p, 'age', 2500) });
  const state = pro2State({ candles: bars, zones, levels, buyShare: share, openInterest: oi, livePartial: context.livePartial }, pro2OptionsFrom(p));
  const value = { bars, zones, levels, state };
  memo = { key, bars, share, oi, value };
  return value;
}

/* ------------------------------------------------------------ рисунок */

const rWords = (r: number): string => `${r > 0 ? '+' : r < 0 ? '−' : ''}${Math.abs(r).toFixed(1).replace('.', ',')}R`;

function markText(mark: Pro2Mark, plan: boolean): string {
  if (!plan) return mark.long ? 'лонг' : 'шорт';
  if (mark.outcome.kind === 'open') return 'в сделке';
  if (mark.outcome.kind === 'target') return `цель ${rWords(mark.outcome.r)}`;
  if (mark.outcome.kind === 'stop') return `стоп ${rWords(mark.outcome.r)}`;
  return `по времени ${rWords(mark.outcome.r)}`;
}

function shapesOf(chart: readonly Candle[], p: IndicatorParams, context: IndicatorContext): IndicatorShape[] {
  const last = chart.at(-1);
  if (!last) return [];
  const { bars, zones, levels, state } = computeOf(chart, p, context);
  const floor = chart[0]!.time;
  const shapes: IndicatorShape[] = [];

  // Зоны — те же полосы, что у PRO: три вложенные на сторону.
  if (boolParam(p, 'zonesOn', true)) {
    const opacity = numberParam(p, 'opacity', 70);
    const band = (upper: readonly (number | null)[], lower: readonly (number | null)[], tone: 'up' | 'down', base: number): void => {
      const points: Array<{ time: number; upper: number; lower: number }> = [];
      for (let i = 0; i < zones.time.length; i += 1) {
        const top = upper[i];
        const bottom = lower[i];
        if (top == null || bottom == null || zones.time[i]! < floor) continue;
        points.push({ time: zones.time[i]!, upper: top, lower: bottom });
      }
      if (points.length > 1) shapes.push({ kind: 'band', points, alpha: zoneAlpha(base, opacity), tone });
    };
    band(zones.buyOuterTop, zones.buyOuterBottom, 'up', ZONE_FADE.outer);
    band(zones.buyStrongTop, zones.buyStrongBottom, 'up', ZONE_FADE.strong);
    band(zones.buyExtremeTop, zones.buyExtremeBottom, 'up', ZONE_FADE.extreme);
    band(zones.sellOuterTop, zones.sellOuterBottom, 'down', ZONE_FADE.outer);
    band(zones.sellStrongTop, zones.sellStrongBottom, 'down', ZONE_FADE.strong);
    band(zones.sellExtremeTop, zones.sellExtremeBottom, 'down', ZONE_FADE.extreme);
  }

  // Карта ликвидности — по желанию: десятки полос поверх свечей.
  if (boolParam(p, 'liquidity', false)) {
    const step = stepOf(bars);
    const atrValue = lastAtr(bars);
    const start = Math.max(floor, earliest(bars, step));
    const below = colorParam(p, 'below', '#00C878');
    const above = colorParam(p, 'above', '#FF3C50');
    const transparency = numberParam(p, 'liquidityOpacity', 50);
    for (const level of levels) {
      if (level.evictedAt !== null || (level.to !== null && level.to < floor)) continue;
      const half = halfHeightOf(atrValue, level.power);
      const alpha = Math.min(0.95, (alphaOf(level.power, level.to === null) * (100 - Math.min(100, Math.max(0, transparency)))) / 50);
      shapes.push({ kind: 'box', from: Math.max(level.from, start), to: level.to, low: level.price - half, high: level.price + half, alpha, tone: level.side === 'above' ? 'down' : 'up', color: level.side === 'above' ? above : below });
    }
  }

  // Отметки: точка снятия, вход, полосы до цели и до стопа — до выхода. «Выкл.» — только стрелка.
  const plan = pro2PlanShown(p);
  // Заливка — только у идущей и последней закрытой сделки: у всех прошлых она залила бы полграфика.
  const filled = new Set(state.marks.slice(-2));
  for (const mark of state.marks) {
    if (mark.time < floor) continue;
    if (plan) {
      const to = mark.outcome.kind === 'open' ? null : mark.outcome.exitTime;
      const fresh = filled.has(mark);
      if (fresh) {
        shapes.push({ kind: 'box', from: mark.time, to, low: Math.min(mark.entry, mark.target), high: Math.max(mark.entry, mark.target), alpha: 0.08, tone: 'up', color: UP });
        shapes.push({ kind: 'box', from: mark.time, to, low: Math.min(mark.entry, mark.stop), high: Math.max(mark.entry, mark.stop), alpha: 0.08, tone: 'down', color: DOWN });
      }
      const end = to ?? last.time;
      const alpha = fresh ? 0.9 : 0.4;
      shapes.push({ kind: 'line', from: { time: mark.time, price: mark.target }, to: { time: end, price: mark.target }, tone: 'up', color: UP, width: 1.2, alpha, ...(to === null ? { label: `цель ${priceWords(mark.target)}` } : {}) });
      shapes.push({ kind: 'line', from: { time: mark.time, price: mark.stop }, to: { time: end, price: mark.stop }, tone: 'down', color: DOWN, width: 1.2, alpha, ...(to === null ? { label: `стоп ${priceWords(mark.stop)}` } : {}) });
    }
    if (mark.sweepTime >= floor) shapes.push({ kind: 'dot', time: mark.sweepTime, price: mark.sweepPrice, tone: mark.long ? 'up' : 'down', color: SWEEP, radius: 3.5 });
    shapes.push({ kind: 'mark', time: mark.time, price: mark.entry, tone: mark.long ? 'up' : 'down', text: markText(mark, plan) });
  }

  // Ждём слом: где закрытие даст вход и где снятие отменится.
  for (const wait of state.waiting) {
    if (!wait.passes || wait.sweepTime < floor) continue;
    shapes.push({ kind: 'dot', time: wait.sweepTime, price: wait.sweepPrice, tone: wait.long ? 'up' : 'down', color: SWEEP, radius: 4 });
    shapes.push({ kind: 'line', from: { time: wait.sweepTime, price: wait.trigger }, to: { time: last.time, price: wait.trigger }, tone: wait.long ? 'up' : 'down', color: SWEEP, width: 1.4, dashed: true, glow: true, label: `слом: закрытие ${wait.long ? 'выше' : 'ниже'} ${priceWords(wait.trigger)} — вход` });
    shapes.push({ kind: 'line', from: { time: wait.sweepTime, price: wait.extreme }, to: { time: last.time, price: wait.extreme }, tone: wait.long ? 'down' : 'up', color: '#A9AFD6', width: 1, dashed: true, alpha: 0.6, label: `${wait.long ? 'ниже' : 'выше'} — отмена` });
  }
  return shapes;
}

/* ------------------------------------------------------------ табло */

const plural = (n: number, forms: readonly [string, string, string]): string => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  return forms[mod10 === 1 && mod100 !== 11 ? 0 : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? 1 : 2];
};

function readoutOf(chart: readonly Candle[], p: IndicatorParams, context: IndicatorContext): ReadoutRow[] {
  if (!boolParam(p, 'table', true)) return [];
  const last = chart.at(-1);
  if (!last) return [];
  const { bars, zones, state } = computeOf(chart, p, context);
  const options = pro2OptionsFrom(p);
  const pct = (share: number | null, long: boolean): string => (share === null ? 'нет данных' : `${Math.round((long ? share : 1 - share) * 100)} %`);

  const plan = pro2PlanShown(p);
  const open = plan ? state.marks.find((mark) => mark.outcome.kind === 'open') : undefined;
  const waiting = state.waiting.find((wait) => wait.passes) ?? state.waiting[0];
  const reading = zoneReading(bars, zones);
  let situation: Situation;
  if (open) {
    situation = {
      title: `В сделке: ${open.long ? 'лонг' : 'шорт'}`,
      tone: open.long ? 'bull' : 'bear',
      text: `Вход ${priceWords(open.entry)} (${whenWords(open.time)}) после снятия ${priceWords(open.sweepPrice)}. Стоп ${priceWords(open.stop)}, цель ${priceWords(open.target)} (1 : ${String(options.target).replace('.', ',')}). Сейчас ${rWords(open.outcome.r)}.`,
    };
  } else if (waiting && waiting.passes) {
    situation = {
      title: 'Сняли уровень — ждём слом',
      tone: 'warn',
      text: `${waiting.long ? 'Покупки' : 'Продажи'} по рынку на свече снятия — ${pct(waiting.share, waiting.long)}. Закрытие ${waiting.long ? 'выше' : 'ниже'} ${priceWords(waiting.trigger)} в ближайшие ${waiting.barsLeft} ${plural(waiting.barsLeft, ['свечу', 'свечи', 'свечей'])} — вход в ${waiting.long ? 'лонг' : 'шорт'}; ${waiting.long ? 'ниже' : 'выше'} ${priceWords(waiting.extreme)} — отмена.`,
    };
  } else if (waiting) {
    situation = {
      title: 'Снятие без перевеса',
      tone: 'muted',
      text: `В зоне сняли уровень, но ${waiting.long ? 'покупки' : 'продажи'} по рынку на этой свече — ${pct(waiting.share, waiting.long)}${options.push !== null ? `, меньше ${Math.round(options.push * 100)} %` : ''}. По правилу не входим.`,
    };
  } else if (reading.side !== 'none') {
    situation = {
      title: reading.side === 'buy' ? 'В зоне покупок — ждём снятия' : 'В зоне продаж — ждём снятия',
      tone: 'muted',
      text: `Цена ${reading.side === 'buy' ? 'дёшево' : 'дорого'} относительно недавней истории. Вход будет, когда свеча проколет уровень ${options.level === 'map' ? 'карты ликвидности' : `крайнего ${reading.side === 'buy' ? 'минимума' : 'максимума'} ${options.swingBars} свечей`} и закроется обратно${options.push !== null ? `, с перевесом ${reading.side === 'buy' ? 'покупок' : 'продаж'} по рынку от ${Math.round(options.push * 100)} %` : ''}${options.confirm ? ', а затем случится слом' : ''}.`,
    };
  } else {
    situation = { title: 'Вне зон', tone: 'muted', text: 'Сигнал бывает только в зоне покупок или продаж. Сейчас цена посередине — ждём.' };
  }

  const rows: ReadoutRow[] = [...situationRows(situation)];
  if (options.push !== null && !state.hasShare) {
    rows.push({ label: 'Внимание', value: 'Нет данных о покупках по рынку (Binance по этой монете) — перевес проверить нечем, отметок не будет. Выключите условие в настройках.', full: true, tone: 'down' });
  }

  // Счёт за выбранный период — по сделкам, которые уже закончились.
  const period = selectParam(p, 'period', '180');
  const days = period === 'all' ? null : Number(period);
  const since = days === null ? -Infinity : last.time - days * DAY;
  const inPeriod = state.marks.filter((mark) => mark.time >= since);
  const periodWords = days === null ? 'всё загруженное' : days === 30 ? 'месяц' : days === 90 ? '3 месяца' : days === 180 ? '6 месяцев' : `${days} дней`;
  rows.push({ label: `Счёт за ${periodWords}`, value: '', heading: true });
  if (inPeriod.length === 0) {
    rows.push({ label: plan ? 'Сделок по правилу' : 'Сигналов', value: 'пока нет', tone: 'muted' });
  } else if (!plan) {
    const longs = inPeriod.filter((mark) => mark.long).length;
    rows.push({ label: 'Сигналов', value: `${inPeriod.length} · лонг ${longs} · шорт ${inPeriod.length - longs}` });
  } else {
    const closed = inPeriod.filter((mark) => mark.outcome.kind !== 'open');
    const wins = closed.filter((mark) => mark.outcome.r > 0).length;
    const total = closed.reduce((sum, mark) => sum + mark.outcome.r, 0);
    rows.push({ label: 'Сделок по правилу', value: closed.length === 0 ? 'ещё идут' : `${closed.length} · в плюс ${wins} (${Math.round((wins / closed.length) * 100)} %)` });
    if (closed.length > 0) rows.push({ label: 'Итог', value: `${rWords(total)} · на сделку ${rWords(total / closed.length)}`, tone: total > 0 ? 'up' : total < 0 ? 'down' : 'muted' });
    rows.push({ label: 'Учтено', value: `стоп, цель 1:${String(options.target).replace('.', ',')}, выход через ${options.hold} ${plural(options.hold, ['свечу', 'свечи', 'свечей'])}, издержки ${String(options.cost).replace('.', ',')} %`, tone: 'muted', full: true });
  }

  // Последние сделки — строкой каждая: когда, куда и чем кончилась.
  const recent = inPeriod.slice(-6).reverse();
  if (recent.length > 0) {
    rows.push({ label: plan ? 'Последние сделки' : 'Последние сигналы', value: '', heading: true });
    for (const mark of recent) {
      const side = mark.long ? 'лонг' : 'шорт';
      const value = !plan ? `${side} по ${priceWords(mark.entry)}` : `${side} · ${markText(mark, true)}`;
      const tone = !plan ? (mark.long ? 'up' : 'down') : mark.outcome.kind === 'open' ? undefined : mark.outcome.r > 0 ? 'up' : 'down';
      rows.push({ label: whenWords(mark.time), value, ...(tone ? { tone } : {}) });
    }
  }
  return rows;
}

const LEGEND: readonly ReadoutLegendItem[] = [
  { sample: 'soft', term: 'Зелёные и красные полосы', text: 'зоны «дёшево» и «дорого» — те же, что у PRO: чем глубже, тем плотнее.' },
  { sample: 'strong', term: 'Голубая точка', text: 'свеча, где в зоне сняли уровень ликвидности и закрылись обратно.' },
  { sample: 'dashed', term: 'Голубой пунктир', text: 'ждём слом: закрытие за этой ценой даст вход. Серый пунктир — где снятие отменится.' },
  { sample: 'solid', term: 'Треугольник с подписью', text: 'вход по правилу и чем кончилась сделка: «цель +1,9R», «стоп −1,0R», «по времени» или «в сделке». При «Риск : прибыль — выкл.» — только стрелка и сторона.' },
  { sample: 'faded', term: 'Полосы от входа', text: 'зелёная — до цели, красная — до стопа; тянутся до выхода.' },
];

/* ------------------------------------------------------------ настройки */

const PARAMS: readonly IndicatorParamSpec[] = [
  {
    key: 'side',
    label: 'Стороны',
    hint: 'Какие входы показывать. Замер шёл по обеим.',
    type: 'select',
    default: 'both',
    options: [
      { value: 'both', label: 'Лонг и шорт' },
      { value: 'long', label: 'Только лонг' },
      { value: 'short', label: 'Только шорт' },
    ],
  },
  {
    key: 'sweepLevel',
    label: 'Что снимают',
    hint: 'Уровень, за которым стояли стопы: из карты ликвидности (по умолчанию) или просто крайний минимум/максимум за несколько свечей. В замере разницы почти не было.',
    type: 'select',
    default: 'map',
    options: [
      { value: 'map', label: 'Уровень карты ликвидности' },
      { value: 'swing', label: 'Минимум / максимум свечей' },
    ],
  },
  { key: 'swingBars', label: 'Свечей для минимума / максимума', hint: 'Только для «Минимум / максимум свечей»: за сколько прошлых свечей брать крайнюю цену.', type: 'number', default: PRO2_DEFAULTS.swingBars, min: 3, max: 200, step: 1 },
  { key: 'confirm', label: 'Ждать слом', hint: 'После снятия ждать закрытия за свечой снятия — выше её максимума у лонга. Без слома вход сразу по закрытию свечи снятия: в замере так было хуже.', type: 'boolean', default: PRO2_DEFAULTS.confirm },
  { key: 'confirmBars', label: 'Свечей на слом', hint: 'Сколько свечей после снятия ждать слома. Не дождались — снятие не в счёт.', type: 'number', default: PRO2_DEFAULTS.confirmBars, min: 1, max: 50, step: 1 },
  { key: 'pushOn', label: 'Перевес покупок по рынку', hint: 'Входить, только если на свече снятия покупки по рынку перевесили продажи (у шорта — наоборот). Единственное объёмное условие, удержавшееся на шести годах. Данные — сделки контрактов Binance.', type: 'boolean', default: true },
  { key: 'push', label: 'Перевес не меньше, %', hint: 'Какая доля оборота свечи снятия должна быть покупками по рынку. В замере: 50 % — ноль, 52,5 % — +0,07R, 55 % — +0,20R, 57,5 % — +0,33R, но сделок втрое меньше.', type: 'number', default: Math.round((PRO2_DEFAULTS.push ?? 0.55) * 1000) / 10, min: 50, max: 75, step: 2.5 },
  { key: 'volumeOn', label: 'Всплеск объёма на снятии', hint: 'Входить, только если объём свечи снятия больше среднего. На шести годах это условие ничего не дало — выключено.', type: 'boolean', default: false },
  { key: 'volume', label: 'Объём не меньше, ×', hint: 'Во сколько раз объём свечи снятия больше среднего за 20 свечей.', type: 'number', default: 2, min: 1, max: 6, step: 0.25 },
  { key: 'flush', label: 'Позиции выбило', hint: 'Входить, только если на снятии открытый интерес резко упал при всплеске объёма. За год выглядело хорошо, на шести годах — в минусе.', type: 'boolean', default: false },
  { key: 'trend', label: 'Только по направлению 4ч', hint: 'Лонг — только когда 4-часовые свечи выше своей средней EMA50, шорт — когда ниже. В замере ухудшало итог: у зоны покупок тренд обычно вниз.', type: 'boolean', default: false },
  {
    key: 'rr',
    label: 'Риск : прибыль',
    hint: 'Во сколько раз цель дальше стопа — у каждой отметки рисуются стоп и цель, а табло считает итог. «Выкл.» — только стрелка сигнала, без стопа и цели. Замер шёл при 1 : 2.',
    type: 'select',
    default: '2',
    options: PRO2_RR.map((value) => ({ value, label: value === 'off' ? 'Выкл. — только стрелка' : `1 : ${value.replace('.', ',')}` })),
  },
  { key: 'stopPad', label: 'Запас стопа за тенью, ATR', hint: 'Насколько стоп дальше тени свечи снятия, в долях среднего размаха свечи. Ближе половины ATR стоп не ставится.', type: 'number', default: PRO2_DEFAULTS.stopPad, min: 0, max: 3, step: 0.05 },
  { key: 'hold', label: 'Выход по времени, свечей', hint: 'Если ни стоп, ни цель не сработали — сделка закрывается через столько свечей. Для счёта в табло.', type: 'number', default: PRO2_DEFAULTS.hold, min: 2, max: 1000, step: 1 },
  { key: 'cost', label: 'Издержки на сделку, %', hint: 'Комиссия и проскальзывание за вход и выход вместе — вычитаются в счёте табло.', type: 'number', default: PRO2_DEFAULTS.cost, min: 0, max: 2, step: 0.01 },
  {
    key: 'period',
    label: 'Счёт в табло за',
    hint: 'За какой срок табло считает сделки и показывает последние из них. Отметки на графике видны за всё загруженное — прокрутите график влево.',
    type: 'select',
    default: '180',
    options: [
      { value: '30', label: 'Месяц' },
      { value: '90', label: '3 месяца' },
      { value: '180', label: '6 месяцев' },
      { value: 'all', label: 'Всё загруженное' },
    ],
  },
  { key: 'zonesOn', label: 'Показывать зоны', hint: 'Полосы «дёшево» и «дорого». Сигналы считаются по ним в любом случае.', type: 'boolean', default: true },
  { key: 'opacity', label: 'Прозрачность зон, %', hint: 'Больше процент — бледнее, свечи под зонами видно лучше.', type: 'number', default: 70, min: 0, max: 99, step: 5 },
  { key: 'liquidity', label: 'Карта ликвидности', hint: 'Показывать уровни, за которыми стоят стопы. Считаются они в любом случае.', type: 'boolean', default: false },
  { key: 'levels', label: 'Максимум уровней на карте', hint: 'Сколько уровней держать разом. Влияет и на сигналы: снять можно только уровень с карты.', type: 'number', default: 400, min: 5, max: 400, step: 5 },
  { key: 'age', label: 'Возраст уровня, свечей', hint: 'Сколько свечей уровень помнится. Влияет и на сигналы.', type: 'number', default: 2500, min: 50, max: 5000, step: 50 },
  { key: 'below', label: 'Цвет ликвидности снизу', hint: 'Каким цветом рисовать скопления стопов под ценой.', type: 'color', default: '#00C878' },
  { key: 'above', label: 'Цвет ликвидности сверху', hint: 'Каким цветом рисовать скопления стопов над ценой.', type: 'color', default: '#FF3C50' },
  { key: 'liquidityOpacity', label: 'Прозрачность ликвидности, %', hint: 'Насколько густо залита карта. Больше процент — бледнее, и свечи под ней видно лучше.', type: 'number', default: 50, min: 0, max: 100, step: 5 },
  { key: 'notifySweep', label: 'Оповещать о снятии', hint: 'Когда включены уведомления — сообщать ещё и о снятии уровня, прошедшем условия: «ждём слом». Сам вход сообщается всегда.', type: 'boolean', default: false },
  TABLE_PARAM,
];

export const ZONES_PRO2: IndicatorDefinition = {
  id: ZONES_PRO2_ID,
  name: 'Выгодные зоны PRO 2',
  description:
    'Вход по снятию ликвидности у зоны: в зоне сняли уровень и закрылись обратно, покупки по рынку перевесили, затем слом. ' +
    'Со стопом и целью у отметки, табло считает итог по истории. Лучше всего проверено на часах.',
  author: 'specter',
  placement: 'overlay',
  params: PARAMS,
  outputs: [],
  derivatives: ['futuresBuyShare', 'oi'],
  deepOn: ['5m', '15m', '30m', '1H', '2H', '4H', '1D'],
  warmup: () => ZONE_WARMUP,
  calc: () => ({}),
  shapes: shapesOf,
  readout: readoutOf,
  legend: LEGEND,
  label: (p) => {
    const parts = ['PRO 2'];
    if (boolParam(p, 'pushOn', true)) parts.push(`перевес ${String(numberParam(p, 'push', 55)).replace('.', ',')} %`);
    if (!boolParam(p, 'confirm', true)) parts.push('без слома');
    return parts.join(' · ');
  },
};
